import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import UAUCAllHeader from "./components/UAUCAllHeader";
import UAUCAllAdvancedFilters, {
  EMPTY_ADV,
} from "./components/UAUCAllAdvancedFilters";
import UAUCAssigneeSection from "./components/UAUCAssigneeSection";
import UAUCLoaderState from "./components/UAUCLoaderState";
import {
  getAssigneeIds,
  getAssigneeNameById,
  getDueMeta,
  getOpenStartDate,
  isClosedStatus,
  normalizeHazards,
  parseDateTimeBoundary,
  safeFetchJSON,
} from "../utils/uaucAllUtils";

const OBSERVER_BOXSHOW = ["userId", "designation", "emailId", "phone"];

export default function UAUCAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All Observations",
}) {
  const [data, setData] = useState([]);
  const [resolvedData, setResolvedData] = useState([]);
  const [query, setQuery] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState(EMPTY_ADV);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const abortRef = useRef(null);
  const navigate = useNavigate();

  const openEdit = useCallback(
    (item) => {
      if (!item?._id) return;
      navigate(`/edit/uauc/${item._id}`);
    },
    [navigate],
  );

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const url = new URL(apiBase + "/uauc");
        url.searchParams.set("project", projectId);

        const response = await safeFetchJSON(url.toString(), {
          signal: ac.signal,
        });

        const list = (
          Array.isArray(response) ? response : response?.data || []
        ).filter((item) => {
          const project = item?.project;
          const id = typeof project === "object" ? project?._id : project;
          return String(id || "") === String(projectId);
        });

        setData(list);
        setResolvedData(list.filter((item) => isClosedStatus(item?.status)));

        const uniqueAssigneeIds = [
          ...new Set(
            list.flatMap((item) => getAssigneeIds(item)).filter(Boolean),
          ),
        ];

        const ratingResults = await Promise.allSettled(
          uniqueAssigneeIds.map(async (id) => {
            const rating = await safeFetchJSON(`${apiBase}/uauc/rating/${id}`, {
              signal: ac.signal,
            });

            return [id, rating];
          }),
        );

        const nextRatings = {};
        ratingResults.forEach((result) => {
          if (result.status === "fulfilled") {
            const [id, rating] = result.value;
            nextRatings[id] = rating;
          }
        });

        setRatings(nextRatings);
      } catch (error) {
        if (error.name !== "AbortError") {
          setErr(error.message || "Failed to load observations.");
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId]);

  const assigneeOptions = useMemo(() => {
    const map = new Map();

    for (const item of data) {
      for (const id of getAssigneeIds(item)) {
        if (!map.has(id)) {
          map.set(id, getAssigneeNameById(item, id) || "Unnamed");
        }
      }
    }

    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();

    return data
      .filter((item) => {
        if (!search) return true;

        const text = [
          item?.observer?.name,
          item?.area,
          normalizeHazards(item?.hazards),
          item?.observation,
          item?.status,
          item?.riskValue?.severity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return text.includes(search);
      })
      .filter((item) => {
        if (!assignedToId) return true;

        return getAssigneeIds(item).map(String).includes(String(assignedToId));
      })
      .filter((item) => {
        if (!adv.assignees?.length) return true;

        const ids = getAssigneeIds(item).map(String);
        return adv.assignees.some((id) => ids.includes(String(id)));
      })
      .filter((item) => {
        const opened = getOpenStartDate(item);
        const openedTime = opened ? new Date(opened).getTime() : 0;

        if (adv.from) {
          const from = parseDateTimeBoundary(adv.from, false);
          const fromTime = from ? from.getTime() : 0;
          if (openedTime && openedTime < fromTime) return false;
        }

        if (adv.to) {
          const to = parseDateTimeBoundary(adv.to, true);
          const toTime = to ? to.getTime() : Number.POSITIVE_INFINITY;
          if (openedTime && openedTime > toTime) return false;
        }

        return true;
      })
      .filter((item) => {
        if (!adv.area) return true;

        return String(item?.area || "")
          .toLowerCase()
          .includes(adv.area.toLowerCase());
      })
      .filter((item) => {
        const riskValue = Number(item?.riskValue?.value ?? NaN);

        if (
          adv.minRisk &&
          !Number.isNaN(riskValue) &&
          riskValue < Number(adv.minRisk)
        ) {
          return false;
        }

        if (
          adv.maxRisk &&
          !Number.isNaN(riskValue) &&
          riskValue > Number(adv.maxRisk)
        ) {
          return false;
        }

        return true;
      })
      .filter((item) => {
        if (!adv.severity?.length) return true;

        const severity = String(item?.riskValue?.severity || "").toLowerCase();

        return adv.severity.some(
          (selected) => severity === String(selected).toLowerCase(),
        );
      })
      .filter((item) => {
        if (!adv.status?.length) return true;

        const status = String(item?.status || "").toLowerCase();

        return adv.status.some((selected) =>
          status.includes(String(selected).toLowerCase()),
        );
      })
      .filter((item) => {
        if (adv.onlyOpen && isClosedStatus(item?.status)) return false;
        if (adv.onlyClosed && !isClosedStatus(item?.status)) return false;
        return true;
      })
      .filter((item) => {
        const meta = getDueMeta(item);

        if (adv.overdueOnly && !meta.overdue) return false;
        if (adv.lateOnly && !meta.closedLate) return false;

        return true;
      })
      .sort((a, b) => {
        const openedA = new Date(getOpenStartDate(a) || 0).getTime();
        const openedB = new Date(getOpenStartDate(b) || 0).getTime();

        return openedA - openedB;
      });
  }, [data, query, assignedToId, adv]);

  const groupedByAssignee = useMemo(() => {
    const groups = new Map();

    const addToGroup = (id, name, item) => {
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          name: name || "Unassigned",
          items: [],
        });
      }

      groups.get(id).items.push(item);
    };

    for (const item of filtered) {
      const ids = getAssigneeIds(item);

      if (!ids.length) {
        addToGroup("__unassigned__", "Unassigned", item);
      } else {
        ids.forEach((id) => {
          addToGroup(id, getAssigneeNameById(item, id) || "Unnamed", item);
        });
      }
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.id === "__unassigned__") return -1;
      if (b.id === "__unassigned__") return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);

  const dashboardStats = useMemo(() => {
    const open = filtered.filter(
      (item) => !isClosedStatus(item?.status),
    ).length;
    const closed = filtered.filter((item) =>
      isClosedStatus(item?.status),
    ).length;

    const slaRisk = filtered.filter((item) => {
      const meta = getDueMeta(item);
      return meta.overdue || meta.closedLate;
    }).length;

    return {
      total: filtered.length,
      open,
      closed,
      slaRisk,
    };
  }, [filtered]);

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <UAUCAllHeader
        title={title}
        query={query}
        setQuery={setQuery}
        assignedToId={assignedToId}
        setAssignedToId={setAssignedToId}
        assigneeOptions={assigneeOptions}
        advOpen={advOpen}
        setAdvOpen={setAdvOpen}
        onBack={onBack}
        stats={dashboardStats}
      />

      {advOpen && (
        <UAUCAllAdvancedFilters
          adv={adv}
          setAdv={setAdv}
          assigneeOptions={assigneeOptions}
        />
      )}

      <div className="p-4">
        {loading || err || groupedByAssignee.length === 0 ? (
          <UAUCLoaderState
            loading={loading}
            error={err}
            empty={!loading && !err && groupedByAssignee.length === 0}
          />
        ) : (
          <div className="space-y-5">
            {groupedByAssignee.map((section) => (
              <UAUCAssigneeSection
                key={section.id}
                section={section}
                ratings={ratings}
                observerBoxShow={OBSERVER_BOXSHOW}
                onOpenEdit={openEdit}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
